import { JSX, SyntheticEvent, useState } from 'react';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useGetList } from 'react-admin';

export interface AdminMiniCompanyPickerProps {
    value: string;
    onChange: (companyId: string) => void;
    disabled?: boolean;
}

interface CompanyOption {
    id: string;
    label: string;
}

/**
 * The company this run is about, when NokNok already holds one.
 *
 * **Optional, and that is the decision** (user, 2026-09-11: a picker falling back to typing).
 * Picking one prefills the four company-sourced Nok data points - name, number, address, privacy
 * policy - exactly as the customer composer does from the customer's own company. Leaving it
 * unset leaves those points empty and the admin types them, which is the ordinary case for a
 * business that is not on record.
 *
 * It is an `Autocomplete` rather than a `Select` because the company list is real data: there are
 * thousands, and a dropdown of thousands is a list nobody can use. Typing filters server-side.
 */
export const AdminMiniCompanyPicker: (props: AdminMiniCompanyPickerProps) => JSX.Element =
    (props: AdminMiniCompanyPickerProps): JSX.Element => {
        const [search, setSearch] = useState<string>('');

        const { data, isPending } = useGetList('companies', {
            pagination: { page: 1, perPage: 25 },
            sort: { field: 'name', order: 'ASC' },
            filter: search.trim() === '' ? {} : { name: search.trim() },
        });

        const options: CompanyOption[] = (data ?? []).map((company: any): CompanyOption => ({
            id: String(company.id),
            label: String(company.name ?? `Company ${ company.id }`),
        }));

        const selected: CompanyOption | null = options.find(
            (option: CompanyOption): boolean => option.id === props.value
        ) ?? null;

        return (
            <Stack spacing={ 1.5 } sx={ { width: '100%' } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow)',
                    letterSpacing: 'var(--wf-track)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                } }>
                    { 'About' }
                </Typography>

                <Autocomplete
                    options={ options }
                    value={ selected }
                    loading={ isPending }
                    disabled={ props.disabled }
                    // The list is filtered by the server, so filtering it again in the browser
                    // hides results that came back for the very term the user typed.
                    filterOptions={ (given: CompanyOption[]): CompanyOption[] => given }
                    isOptionEqualToValue={ (option: CompanyOption, chosen: CompanyOption): boolean =>
                        option.id === chosen.id }
                    onInputChange={ (_event: SyntheticEvent, typed: string): void => setSearch(typed) }
                    onChange={ (_event: SyntheticEvent, chosen: CompanyOption | null): void =>
                        props.onChange(chosen?.id ?? '') }
                    renderInput={ (params): JSX.Element => (
                        <TextField { ...params }
                                   size="small"
                                   label="Company"
                                   placeholder="Search, or leave empty"
                                   helperText="Fills in the company details a Nok asks for. Leave empty to type them." />
                    ) }
                />
            </Stack>
        );
    };
